import { Fragment, useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Heading, TableColumnConfig } from '@frontend/design-system';
import { NETWORK_KEY_MAPPINGS_BY_VENDOR } from '../../../../../src/helpers/formattedDentalCoverageTableData';
import { InsuranceReportTable } from '../../../../shared';
import { useDentalInsuranceReportContext } from '../../context/DentalInsuranceReportDetailsContext.provider';
import { SegregatedDataByNetworkType, TableDataType, TableFilterKey, TablesDataType, Vendors } from '../../types/types';
import { getUniqueValuesByKey } from './insurance-reports-table-columns.helper';

type InsuranceReportPrintableAccordionSectionProps = {
  tableId: keyof TablesDataType;
  columns: TableColumnConfig<TableDataType>[];
  vendor: Vendors;
};

type FilterConfigListByNetwork = {
  [K in keyof SegregatedDataByNetworkType]: {
    header: string;
    filteredTableData: TableDataType[];
  }[];
};

export const InsuranceReportPrintableAccordionSection = ({
  tableId,
  columns,
  vendor,
}: InsuranceReportPrintableAccordionSectionProps) => {
  //This is a network name like Delta dental premiere, delta dental ppo and each vendor has different key like network, networkName
  const filterKey = NETWORK_KEY_MAPPINGS_BY_VENDOR[vendor as Vendors]['networkName'] as TableFilterKey;
  const { t } = useTranslation();
  const { segregatedTablesDataByNetwork } = useDentalInsuranceReportContext();

  const filterConfigList = useMemo(() => {
    const filterNetworkData: FilterConfigListByNetwork = {
      in_network: [],
      out_network: [],
    };
    for (const networkType in segregatedTablesDataByNetwork) {
      const tablesData = segregatedTablesDataByNetwork[networkType as keyof SegregatedDataByNetworkType];

      const tableData = (tablesData[tableId] || []) as TableDataType[];
      //Getting all type of network names available
      const uniqueFilterValues = getUniqueValuesByKey(tableData, filterKey);
      if (uniqueFilterValues.length > 1) {
        // Iterate through each filter value and create a filtered table data
        uniqueFilterValues?.forEach((filterValue) => {
          const filteredTableData =
            tableData?.filter((item) => {
              //item is converted record because here item is of tabledatatype which is tuuthfairy type and dentalxchange so filterkey network is not present in tuuthfairy and networkType is not present in dentalxchange
              const value = filterKey in item ? (item as Record<string, string>)?.[filterKey] : '';
              return value?.toString()?.toLowerCase() === filterValue?.toLowerCase();
            }) || [];

          filterNetworkData[networkType as keyof SegregatedDataByNetworkType].push({
            header: filterValue.toUpperCase(),
            filteredTableData,
          });
        });
      } else {
        if (tableData.length) {
          filterNetworkData[networkType as keyof SegregatedDataByNetworkType].push({
            header: '',
            filteredTableData: tableData,
          });
        }
      }
    }

    return filterNetworkData;
  }, [segregatedTablesDataByNetwork]);

  return (
    <>
      <Heading css={{ margin: theme.spacing(2, 0), marginBottom: theme.spacing(3) }} level={2}>
        {t('IN NETWORK')}
      </Heading>

      {filterConfigList['in_network']?.map((filterConfig) => (
        <Fragment key={filterConfig.header}>
          {filterConfig?.header && (
            <Heading css={{ margin: theme.spacing(2, 0) }} level={3}>
              {filterConfig?.header}
            </Heading>
          )}
          {filterConfig.filteredTableData && (
            <InsuranceReportTable<TableDataType>
              key={filterConfig.header}
              tableInstanceId={`insurance-report-${tableId}-${filterConfig.header}`}
              columns={columns}
              tableData={filterConfig.filteredTableData || []}
              isPaginated={false}
            />
          )}
        </Fragment>
      ))}

      {filterConfigList['out_network'].length > 0 && (
        <Heading css={{ margin: theme.spacing(2, 0), marginBottom: theme.spacing(3) }} level={2}>
          {t('OUT OF NETWORK')}
        </Heading>
      )}

      {filterConfigList['out_network'].length > 0 &&
        filterConfigList['out_network']?.map((filterConfig) => (
          <Fragment key={filterConfig.header}>
            {filterConfig?.header && (
              <Heading css={{ margin: theme.spacing(2, 0) }} level={3}>
                {filterConfig?.header}
              </Heading>
            )}
            {filterConfig.filteredTableData && (
              <InsuranceReportTable<TableDataType>
                key={filterConfig.header}
                tableInstanceId={`insurance-report-${tableId}-${filterConfig.header}`}
                columns={columns}
                tableData={filterConfig.filteredTableData || []}
                isPaginated={false}
              />
            )}
          </Fragment>
        ))}
    </>
  );
};
